import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import {
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ShoppingBagIcon,
  CalendarDaysIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";

const CartPage = () => {
  const {
    items,
    removeItem,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
  } = useCart();

  const { user, apiCall } = useAuth();

  const [pets, setPets] = useState([]);
  const [selectedPetId, setSelectedPetId] = useState("");
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const serviceItems = useMemo(() => {
    return items.filter((item) => item.type === "service");
  }, [items]);

  const productItems = useMemo(() => {
    return items.filter((item) => item.type !== "service");
  }, [items]);

  const hasServices = serviceItems.length > 0;
  const hasProducts = productItems.length > 0;

  useEffect(() => {
    loadPets();
  }, [user]);

  const loadPets = async () => {
    if (!user || !apiCall) return;

    try {
      const response = await apiCall("/pets");
      const loadedPets = response?.data || response?.pets || [];

      setPets(Array.isArray(loadedPets) ? loadedPets : []);

      if (Array.isArray(loadedPets) && loadedPets.length > 0) {
        setSelectedPetId((prev) => prev || loadedPets[0].id);
      }
    } catch (error) {
      console.error("Load pets error:", error);
      setPets([]);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(price || 0));
  };

  const getItemCompanyId = (item) => {
    return item.company_id || item.companyId || item.companyID || "";
  };

  const getServiceId = (item) => {
    return item.service_id || item.serviceId || item.id;
  };

  const buildBookingDateTime = () => {
    if (!bookingDate || !bookingTime) {
      return "";
    }

    const localDate = new Date(`${bookingDate}T${bookingTime}:00`);

    if (Number.isNaN(localDate.getTime())) {
      return "";
    }

    return localDate.toISOString();
  };

  const handleSubmitBookingRequests = async () => {
    setSubmitError("");
    setSubmitSuccess("");

    if (!user) {
      setSubmitError("Please sign in to book a service.");
      return;
    }

    if (!hasServices) {
      setSubmitError("There are no services in your cart.");
      return;
    }

    if (!selectedPetId) {
      setSubmitError("Please select a pet.");
      return;
    }

    const dateTime = buildBookingDateTime();

    if (!dateTime) {
      setSubmitError("Please select booking date and time.");
      return;
    }

    const servicesWithoutCompany = serviceItems.filter(
      (item) => !getItemCompanyId(item),
    );

    if (servicesWithoutCompany.length > 0) {
      setSubmitError(
        "Some services do not have company_id. Please check add-to-cart data.",
      );
      return;
    }

    setSubmitting(true);

    try {
      const createdBookings = [];

      for (const item of serviceItems) {
        const quantity = Number(item.quantity || 1);

        for (let index = 0; index < quantity; index += 1) {
          const response = await apiCall("/bookings", {
            method: "POST",
            body: JSON.stringify({
              company_id: getItemCompanyId(item),
              service_id: getServiceId(item),
              pet_id: selectedPetId,
              date_time: dateTime,
              notes:
                notes ||
                `Booking request from cart for ${item.name || item.title}`,
            }),
          });

          if (!response?.success) {
            throw new Error(response?.error || "Failed to create booking");
          }

          createdBookings.push(response.data);
        }
      }

      serviceItems.forEach((item) => {
        removeItem(item.id, item.type);
      });

      setSubmitSuccess(
        `Booking request sent successfully. Created: ${createdBookings.length}`,
      );

      setNotes("");
    } catch (error) {
      console.error("Create booking error:", error);
      setSubmitError(error.message || "Failed to send booking request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ShoppingBagIcon className="mx-auto h-24 w-24 text-gray-300" />

            <h1 className="mt-6 text-3xl font-bold text-gray-900">
              Cart is Empty
            </h1>

            <p className="mt-4 text-lg text-gray-600">
              Add products or services to your cart to continue shopping
            </p>

            <div className="mt-8">
              <Link to="/marketplace" className="btn-primary text-lg px-8 py-4">
                Go Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Cart ({getTotalItems()} items)
          </h1>

          <p className="mt-2 text-gray-600">
            Check selected products and services before placing your request
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Items in Cart
                  </h2>

                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-orange-500 hover:text-red-700 text-sm font-medium"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {items.map((item) => (
                  <div key={`${item.id}-${item.type}`} className="p-6">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">
                          {item.type === "service" ? "🛍️" : "📦"}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {item.name || item.title}
                        </h3>

                        <p className="text-sm text-gray-500">
                          {item.company || item.provider}
                        </p>

                        <p className="text-sm text-gray-500 capitalize">
                          {item.type === "service" ? "Service" : "Product"}
                        </p>

                        {item.description && (
                          <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.type,
                              item.quantity - 1,
                            )
                          }
                          className="p-1 rounded-full hover:bg-gray-100"
                          disabled={item.quantity <= 1}
                        >
                          <MinusIcon className="h-4 w-4 text-gray-400" />
                        </button>

                        <span className="w-12 text-center font-medium">
                          {item.quantity}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.type,
                              item.quantity + 1,
                            )
                          }
                          className="p-1 rounded-full hover:bg-gray-100"
                        >
                          <PlusIcon className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>

                      <div className="text-right">
                        <div className="text-lg font-semibold text-gray-900">
                          {formatPrice(item.price * item.quantity)}
                        </div>

                        {item.quantity > 1 && (
                          <div className="text-sm text-gray-500">
                            {formatPrice(item.price)} per item
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item.id, item.type)}
                        className="p-2 text-orange-500 hover:text-red-700 hover:bg-orange-50 rounded-full"
                        title="Remove from cart"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {hasProducts && (
              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
                <h3 className="font-black text-orange-700 text-sm uppercase">
                  Products checkout is not ready yet
                </h3>

                <p className="text-sm text-orange-700 mt-1">
                  Service booking requests are available now. Product orders can
                  be connected in the next step.
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Order Total
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Items ({getTotalItems()} items)
                  </span>

                  <span className="font-medium">
                    {formatPrice(getTotalPrice())}
                  </span>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">
                      Total
                    </span>

                    <span className="text-lg font-semibold text-gray-900">
                      {formatPrice(getTotalPrice())}
                    </span>
                  </div>
                </div>
              </div>

              {hasServices && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarDaysIcon className="h-5 w-5 text-orange-500" />

                    <h3 className="text-sm font-black uppercase tracking-wider text-gray-900">
                      Service Booking Request
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">
                        Pet
                      </label>

                      <select
                        value={selectedPetId}
                        onChange={(event) =>
                          setSelectedPetId(event.target.value)
                        }
                        className="w-full input-field text-sm"
                      >
                        {pets.length === 0 ? (
                          <option value="">No pets found</option>
                        ) : (
                          pets.map((pet) => (
                            <option key={pet.id} value={pet.id}>
                              {pet.name}
                            </option>
                          ))
                        )}
                      </select>

                      {pets.length === 0 && (
                        <Link
                          to="/profile"
                          className="inline-block mt-2 text-xs font-bold text-orange-500 hover:text-orange-700"
                        >
                          Add pet in profile
                        </Link>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">
                        Date
                      </label>

                      <input
                        type="date"
                        value={bookingDate}
                        onChange={(event) => setBookingDate(event.target.value)}
                        className="w-full input-field text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">
                        Time
                      </label>

                      <input
                        type="time"
                        value={bookingTime}
                        onChange={(event) => setBookingTime(event.target.value)}
                        className="w-full input-field text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1">
                        Notes
                      </label>

                      <textarea
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        rows={3}
                        placeholder="Additional information for company..."
                        className="w-full input-field text-sm resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="mt-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold p-3">
                  {submitError}
                </div>
              )}

              {submitSuccess && (
                <div className="mt-4 rounded-xl bg-green-50 border border-green-100 text-green-700 text-sm font-bold p-3">
                  {submitSuccess}
                </div>
              )}

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={handleSubmitBookingRequests}
                  disabled={submitting || !hasServices}
                  className="w-full btn-primary text-center text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />

                  {submitting ? "Sending..." : "Send Booking Request"}
                </button>

                <Link to="/marketplace" className="w-full btn-secondary text-center">
                  Continue Shopping
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg
                    className="h-4 w-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>

                  <span>Secure Request</span>
                </div>

                <p className="mt-1 text-xs text-gray-500">
                  Your request will be sent to the company for approval.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Maybe you'll be interested in
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <span className="text-4xl">🐕</span>
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Recommended Product {item}
                  </h3>

                  <p className="text-sm text-gray-600 mb-3">
                    Product or service description
                  </p>

                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-primary-500">
                      {formatPrice(1500 + item * 100)}
                    </span>

                    <button type="button" className="btn-primary text-sm px-4 py-2">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;